import { Link } from 'react-router-dom';
import { useState } from 'react';
import { handlePayment as handleStripePayment } from '@lib/stripe';
import Breadcrumb from '@/components/common/Breadcrumb';

const ServicesPricing = () => {
  const [loading, setLoading] = useState<'consultation' | 'booking' | 'guide' | 'meeting' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePayment = async (type: 'consultation' | 'booking' | 'guide' | 'meeting') => {
    try {
      console.log('Starting payment for:', type);
      setError(null);
      setLoading(type);
      await handleStripePayment(type);
    } catch (error) {
      console.error('Payment error details:', { error, type, timestamp: new Date().toISOString() });
      
      // Extract error message
      let errorMessage = 'Failed to initiate payment. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Show user-friendly alert
      alert(`Payment Error: ${errorMessage}\n\nIf this continues, please contact support.`);
      
      setError(errorMessage);
      setLoading(null);
    }
  };

  return (
    <div className="container-custom">
      <Breadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Services & Pricing' }
        ]}
      />

      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Services & Pricing
        </h1>
        <p className="text-gray-600">
          Choose to level of support that fits your needs. All payments are processed securely through Stripe.
        </p>
      </div>

      {/* Error display - optional, can be styled better */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Payment Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Choose Your Service</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {false && (
            <div className="card p-8 flex flex-col">
              <div className="flex-grow">
                <div className="text-4xl mb-4">📖</div>
                <h2 className="text-2xl font-bold mb-2">Free Guide</h2>
                <div className="text-3xl font-bold text-gray-900 mb-4">$0</div>
                <p className="text-gray-600 mb-6">
                  Explore our comprehensive guides on hospitals, visa requirements, and travel tips at your own pace.
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Access to hospital directory</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Visa guide</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Hospital detail pages</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Price comparisons</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-gray-400">Personal consultation</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-gray-400">Appointment booking</span>
                  </li>
                </ul>
              </div>
              <Link
                to="/hospitals"
                className="btn-secondary w-full"
              >
                Browse Hospitals
              </Link>
            </div>
          )}

          <div className="card p-6 flex flex-col">
            <div className="flex-grow">
              <div className="text-3xl mb-3">📋</div>
              <h2 className="text-xl font-bold mb-2">Medical Guide</h2>
              <div className="text-2xl font-bold text-primary-600 mb-1">$9.99</div>
              <div className="text-xs text-gray-500 mb-4">One-time payment</div>
              <p className="text-gray-600 mb-4 text-sm">
                Bilingual medical guide with hospital, department, and doctor information plus registration assistance.
              </p>

              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Personalized hospital guide</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Department & doctor info</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Registration guide & timing</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Transportation cost estimates</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Nearby hotels & prices</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Bilingual (EN/CN) format</span>
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <Link
                to="/sample-guide"
                className="btn-secondary w-full text-sm inline-block text-center"
              >
                View Sample
              </Link>
              <button
                className="btn-primary w-full text-sm"
                disabled={loading === 'guide'}
                onClick={() => handlePayment('guide')}
              >
                {loading === 'guide' ? 'Processing...' : 'Get Guide - $9.99'}
              </button>
            </div>
          </div>

          <div className="card pt-8 pb-6 px-6 flex flex-col border-2 border-blue-500 relative">
            <div className="absolute top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                New!
              </span>
            </div>
            <div className="flex-grow">
              <div className="text-3xl mb-3">📹</div>
              <h2 className="text-xl font-bold mb-2">Online Meeting</h2>
              <div className="text-2xl font-bold text-blue-600 mb-1">$19.9</div>
              <div className="text-xs text-gray-500 mb-4">30-minute session</div>
              <p className="text-gray-600 mb-4 text-sm">
                Get instant answers to your questions about visa, hospitals, hotels, and other services through a video call with our experts.
              </p>

              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>30-minute video consultation</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Bilingual support (English/Chinese)</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Any topic: visa, hospitals, hotels</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Multiple meeting platforms</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Choose 3 time slots, we confirm</span>
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <Link
                to="/meeting-booking"
                className="btn-primary w-full text-sm block text-center"
              >
                Book Meeting - $19.9
              </Link>
            </div>
          </div>

          <div className="card pt-8 pb-6 px-6 flex flex-col border-primary-500 border-2 relative">
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
              <span className="bg-primary-600 text-white text-sm font-semibold px-4 py-1 rounded-full">
                Most Popular
              </span>
            </div>
            <div className="flex-grow">
              <div className="text-4xl mb-4">💬</div>
              <h2 className="text-2xl font-bold mb-2">Consultation</h2>
              <div className="text-3xl font-bold text-primary-600 mb-1">$99</div>
              <div className="text-sm text-gray-500 mb-6">One-time payment</div>
              <p className="text-gray-600 mb-6">
                Get personalized advice from our experts on which hospitals and doctors best fit your medical condition.
              </p>

              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Everything in Free Guide</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Personal consultation via email</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Hospital & doctor recommendations</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Treatment cost estimates</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Language support tips</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-gray-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-gray-400">Appointment booking</span>
                </li>
              </ul>
            </div>
            <button
              className="btn-primary w-full text-sm"
              disabled={loading === 'consultation'}
              onClick={() => handlePayment('consultation')}
            >
              {loading === 'consultation' ? 'Processing...' : 'Get Consultation - $99'}
            </button>
          </div>

          {false && (
            <div className="card p-8 flex flex-col">
              <div className="flex-grow">
                <div className="text-4xl mb-4">🏥</div>
                <h2 className="text-2xl font-bold mb-2">Full Booking</h2>
                <div className="text-3xl font-bold text-gray-900 mb-1">$300</div>
                <div className="text-sm text-gray-500 mb-6">One-time payment</div>
                <p className="text-gray-600 mb-6">
                  Complete booking service including appointment scheduling, translations, and logistics coordination.
                </p>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Everything in Consultation</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Appointment booking service</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Medical record translation</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Transportation coordination</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>24/7 support during stay</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Hotel booking assistance</span>
                  </li>
                </ul>
              </div>
              <button
                className="btn-primary w-full"
                disabled={loading === 'booking'}
                onClick={() => handlePayment('booking')}
              >
                {loading === 'booking' ? 'Processing...' : 'Book Appointment - $300'}
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Why Choose Our Services?</h2>
        <p className="text-gray-600 mb-8">
          Navigating China's healthcare system as an international patient can be challenging. Our services bridge the language and cultural gaps to ensure you receive quality medical care.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-lg mb-2">Language Barrier</h3>
                <p className="text-gray-600 text-sm">
                  Most Chinese hospitals don't provide English services. Medical records, forms, and doctor consultations are primarily in Chinese, making communication difficult for international patients.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-lg mb-2">Complex Booking</h3>
                <p className="text-gray-600 text-sm">
                  Booking appointments is complex with multiple booking systems, limited online English interfaces, and different timing requirements across hospitals.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-lg mb-2">System Navigation</h3>
                <p className="text-gray-600 text-sm">
                  Understanding which hospital, department, and doctor is right for your condition requires local knowledge that most international patients lack.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-6 md:col-span-2 lg:col-span-3">
            <div className="flex flex-col md:flex-row items-center">
              <div className="flex-grow mb-4 md:mb-0 md:mr-6">
                <h3 className="font-bold text-xl mb-3 text-primary-800">How We Help</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-primary-900"><strong>Medical Guide:</strong> Bilingual guides with hospital, doctor, and registration information to navigate on your own</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-primary-900"><strong>Consultation:</strong> Personal advice on which hospitals and doctors fit your needs best</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-primary-900"><strong>Full Booking:</strong> Complete service handling appointments, translations, and logistics</span>
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="/sample-guide"
                  className="btn-primary inline-block"
                >
                  View Sample Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Price Comparison</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Procedure
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  US Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  China Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Savings
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">MRI Scan</td>
                <td className="px-6 py-4 text-sm text-gray-600">$1,500 - $2,500</td>
                <td className="px-6 py-4 text-sm text-gray-600">$110 - $210</td>
                <td className="px-6 py-4 text-sm font-semibold text-green-600">92-95%</td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">CT Scan</td>
                <td className="px-6 py-4 text-sm text-gray-600">$500 - $1,000</td>
                <td className="px-6 py-4 text-sm text-gray-600">$42 - $85</td>
                <td className="px-6 py-4 text-sm font-semibold text-green-600">92-95%</td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">PET-CT</td>
                <td className="px-6 py-4 text-sm text-gray-600">$2,000 - $3,500</td>
                <td className="px-6 py-4 text-sm text-gray-600">$980 - $1,400</td>
                <td className="px-6 py-4 text-sm font-semibold text-green-600">60-72%</td>
              </tr>
              <tr>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">Cardiac Angiography</td>
                <td className="px-6 py-4 text-sm text-gray-600">$5,000 - $10,000</td>
                <td className="px-6 py-4 text-sm text-gray-600">$500 - $1,000</td>
                <td className="px-6 py-4 text-sm font-semibold text-green-600">90%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          * Prices are estimates and may vary by hospital and specific treatment requirements. Contact individual hospitals for accurate quotes.
        </p>
      </section>

      <section className="mb-12">
        <div className="bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">Secure Payment</h2>
          <p className="text-gray-600 mb-4">
            All payments are processed securely through Stripe. We accept Visa, MasterCard, American Express, and other major credit cards.
          </p>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-8 bg-white rounded border border-gray-300 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-600">VISA</span>
            </div>
            <div className="w-12 h-8 bg-white rounded border border-gray-300 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-600">MC</span>
            </div>
            <div className="w-12 h-8 bg-white rounded border border-gray-300 flex items-center justify-center">
              <span className="text-xs font-semibold text-gray-600">AMEX</span>
            </div>
            <div className="flex items-center text-gray-600">
              <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-sm">SSL Encrypted</span>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="bg-primary-600 text-white rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Need Help Choosing?</h2>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            Not sure which service is right for you? Start with our free guide and upgrade anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/hospitals"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-100 transition-colors"
            >
              Browse Hospitals
            </Link>
            <Link
              to="/visa-guide"
              className="inline-flex items-center justify-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors"
            >
              Read Visa Guide
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPricing;
